import React, { useContext, useState, useMemo, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Autocomplete,
    Chip,
    Alert,
    TableRow,
    TableCell,
    IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { ThemeContext } from 'styled-components';
import { CsrfContext } from '../../../providers/CsrfProvider';
import { createBillRule } from '../../../services/bill/BillRules';
import { useQuery, useQueryClient } from 'react-query';
import { listTemplateParticipants } from '../../../services/template/Participants';
import ParticipantSlider from './ParticipantSlider';
import FormatNumber from '../../../utils/formatters/FormatNumber';

const CreateRuleForm = ({ templateId, isCreating, setIsCreating, disabled, onSuccess }) => {
    const theme = useContext(ThemeContext);
    const { csrfToken } = useContext(CsrfContext);
    const queryClient = useQueryClient();

    const [ruleType, setRuleType] = useState('minus');
    const [label, setLabel] = useState('');
    const [amount, setAmount] = useState('');
    const [rulePercentage, setRulePercentage] = useState('');
    const [selectedParticipants, setSelectedParticipants] = useState([]);
    const [participantPercentages, setParticipantPercentages] = useState({});
    const [participantInputValue, setParticipantInputValue] = useState('');
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const previousRulePercentageRef = useRef(null);

    // Fetch participants for this bill
    const queryKey = useMemo(() => {
        if (!templateId) return ['participants', 'none'];
        const id = String(templateId);
        if (id === 'undefined' || id === 'null' || id === 'none') return ['participants', 'none'];
        return ['participants', id];
    }, [templateId]);

    const { data: participantsData, isLoading: isLoadingParticipants } = useQuery(
        queryKey,
        listTemplateParticipants(csrfToken),
        {
            enabled: !!templateId && !!csrfToken && templateId !== 'none' && isCreating,
            refetchOnMount: true,
            refetchOnWindowFocus: false,
        }
    );

    const participants = Array.isArray(participantsData) ? participantsData : [];

    // Initialize percentages evenly when participants change for percent rules
    useEffect(() => {
        if (ruleType === 'percent' && selectedParticipants.length > 0 && rulePercentage) {
            const rulePct = parseFloat(rulePercentage);
            if (!isNaN(rulePct) && rulePct > 0) {
                // Always redistribute evenly among all participants when count changes
                const equalPercent = rulePct / selectedParticipants.length;
                const newPercentages = {};
                selectedParticipants.forEach(p => {
                    const key = p?.id || p?.email;
                    if (key) {
                        newPercentages[key] = equalPercent;
                    }
                });
                setParticipantPercentages(newPercentages);
            }
        }
    }, [ruleType, selectedParticipants.length, rulePercentage]);

    // Scale participant percentages proportionally when rule percentage changes
    useEffect(() => {
        if (ruleType === 'percent' && selectedParticipants.length > 0 && rulePercentage && previousRulePercentageRef.current != null) {
            const oldRulePct = previousRulePercentageRef.current;
            const newRulePct = parseFloat(rulePercentage);

            if (!isNaN(newRulePct) && !isNaN(oldRulePct) && oldRulePct > 0 && newRulePct !== oldRulePct) {
                const scaleFactor = newRulePct / oldRulePct;
                const scaledPercentages = {};

                selectedParticipants.forEach(p => {
                    const key = p?.id || p?.email;
                    if (key && participantPercentages[key] != null) {
                        scaledPercentages[key] = participantPercentages[key] * scaleFactor;
                    }
                });

                setParticipantPercentages(scaledPercentages);
            }
        }

        previousRulePercentageRef.current = rulePercentage ? parseFloat(rulePercentage) : null;
    }, [rulePercentage]);

    const handleAmountChange = (e) => {
        const value = e.target.value;
        if (value === '' || (!isNaN(value) && parseFloat(value) >= 0)) {
            setAmount(value);
        }
    };

    const handleAmountKeyDown = (e) => {
        // Handle arrow key presses to increment/decrement by 1 while preserving decimals
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            const currentValue = amount === '' ? 0 : parseFloat(amount);
            const newValue = e.key === 'ArrowUp' ? currentValue + 1 : Math.max(0, currentValue - 1);
            setAmount(newValue.toString());
        }
    };

    const calculateTotalPercentage = () => {
        let total = 0;
        selectedParticipants.forEach(p => {
            const key = p?.id || p?.email;
            if (key && participantPercentages[key] != null) {
                total += parseFloat(participantPercentages[key]) || 0;
            }
        });
        return total;
    };

    const handleStartAdd = () => {
        setIsCreating(true);
        setRuleType('minus');
        setLabel('');
        setAmount('');
        setRulePercentage('');
        setSelectedParticipants([]);
        setParticipantPercentages({});
        setError(null);
        setShowAdvanced(false);
    };

    const handleCancel = () => {
        setIsCreating(false);
        setRuleType('minus');
        setLabel('');
        setAmount('');
        setRulePercentage('');
        setSelectedParticipants([]);
        setParticipantPercentages({});
        setError(null);
        setShowAdvanced(false);
    };

    const handleSubmit = async (e) => {
        if (e) {
            e.preventDefault();
        }

        setError(null);

        if (ruleType === 'minus' && (!amount || parseFloat(amount) <= 0)) {
            setError('Amount must be greater than 0');
            return;
        }

        if (ruleType === 'percent' && (!rulePercentage || parseFloat(rulePercentage) <= 0 || parseFloat(rulePercentage) > 100)) {
            setError('Rule percentage must be between 0 and 100');
            return;
        }

        if (ruleType === 'percent' && selectedParticipants.length > 0) {
            const hasPercentages = selectedParticipants.some(p => {
                const key = p?.id || p?.email;
                return key && participantPercentages[key] != null && participantPercentages[key] > 0;
            });

            if (!hasPercentages) {
                const rulePct = parseFloat(rulePercentage);
                if (!isNaN(rulePct) && rulePct > 0) {
                    const equalPercent = rulePct / selectedParticipants.length;
                    const newPercentages = { ...participantPercentages };
                    selectedParticipants.forEach(p => {
                        const key = p?.id || p?.email;
                        if (key && (!(key in newPercentages) || newPercentages[key] === null || newPercentages[key] === undefined)) {
                            newPercentages[key] = equalPercent;
                        }
                    });
                    setParticipantPercentages(newPercentages);
                }
            }
        }

        if (selectedParticipants.length === 0) {
            setError('Rule must have at least one participant');
            return;
        }

        if (ruleType === 'percent') {
            const totalPct = calculateTotalPercentage();
            if (totalPct > 100.01) {
                setError(`Participant percentages cannot exceed 100% (currently ${FormatNumber(totalPct)}%)`);
                return;
            }
            if (totalPct === 0) {
                setError('Percent rule requires at least one participant with a percentage');
                return;
            }
        }

        setSubmitting(true);

        try {
            const participantUserIDs = [];
            const participantEmails = [];

            selectedParticipants.forEach(p => {
                if (p && p.id) {
                    participantUserIDs.push(p.id);
                } else if (p && p.email) {
                    participantEmails.push(p.email);
                }
            });

            if (participantUserIDs.length === 0 && participantEmails.length === 0) {
                setError('Please select at least one participant or enter an email');
                setSubmitting(false);
                return;
            }

            const ruleData = {
                rule_type: ruleType,
                participant_user_ids: participantUserIDs,
            };

            const trimmedLabel = (label || '').trim();
            if (trimmedLabel) {
                ruleData.label = trimmedLabel;
            }

            if (participantEmails.length > 0) {
                ruleData.participant_emails = participantEmails;
            }

            if (ruleType === 'minus') {
                ruleData.amount = parseFloat(amount);
            }

            if (ruleType === 'percent') {
                ruleData.rule_percentage = parseFloat(rulePercentage);

                const percentages = {};
                selectedParticipants.forEach(p => {
                    const key = p.id || p.email;
                    if (key && participantPercentages[key] != null) {
                        const percentKey = p.id || p.email;
                        percentages[percentKey] = participantPercentages[key];
                    }
                });
                ruleData.participant_percentages = percentages;
            }

            await createBillRule(String(templateId), ruleData, csrfToken);

            if (onSuccess) {
                onSuccess();
            }

            handleCancel();
        } catch (err) {
            console.error('Error creating rule:', err);
            setError(err.response?.data?.error || err.message || 'Failed to create rule');
        } finally {
            setSubmitting(false);
        }
    };

    const isValid = () => {
        if (ruleType === 'minus') {
            return amount && parseFloat(amount) > 0 && selectedParticipants.length > 0;
        } else if (ruleType === 'percent') {
            if (!rulePercentage || parseFloat(rulePercentage) <= 0 || selectedParticipants.length === 0) {
                return false;
            }
            // Check if participant percentages add up to rule percentage
            const totalPct = calculateTotalPercentage();
            const rulePct = parseFloat(rulePercentage);
            return Math.abs(totalPct - rulePct) < 0.01; // Allow small floating point differences
        }
        return false;
    };

    return (
        <>
            {/* Header Row (when creating) */}
            {isCreating && (
                <TableRow>
                    <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0, borderBottom: 'none' }}></TableCell>
                    <TableCell colSpan={2} sx={{ pl: 0, pr: 0, py: 1, borderBottom: 'none' }}>
                        <Typography variant="h6" sx={{ color: theme.colors.text, fontWeight: 600 }}>
                            Add Rule
                        </Typography>
                    </TableCell>
                    <TableCell sx={{
                        width: '56px !important',
                        minWidth: '56px !important',
                        maxWidth: '56px !important',
                        p: 0,
                        flexShrink: 0,
                        flexGrow: 0,
                        borderBottom: 'none',
                    }}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                            height: '100%'
                        }}>
                            <IconButton
                                onClick={handleCancel}
                                size="small"
                                sx={{
                                    color: 'inherit',
                                    padding: '4px',
                                    '&:hover': {
                                        backgroundColor: theme.colors.overlayHover,
                                    },
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </TableCell>
                </TableRow>
            )}

            {/* Form Row (when creating) */}
            {isCreating && (
                <TableRow>
                    <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }}></TableCell>
                    <TableCell colSpan={2} sx={{ pl: 0, pr: 0, py: 2 }}>
                        <Box
                            component="form"
                            onSubmit={handleSubmit}
                            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                        >
                            {error && (
                                <Alert severity="error" onClose={() => setError(null)}>
                                    {error}
                                </Alert>
                            )}

                            <TextField
                                label="Label (recommended)"
                                placeholder="e.g. pet fee"
                                value={label}
                                onChange={(e) => setLabel(e.target.value)}
                                fullWidth
                                helperText="Label rules (e.g. pet fee, parking) so they're easier to recognize."
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: theme.colors.text,
                                        fontSize: '16px',
                                        '& fieldset': {
                                            borderColor: theme.colors.secondary || theme.colors.brandSecondary,
                                        },
                                        '&:hover fieldset': { borderColor: theme.colors.text },
                                        '&.Mui-focused fieldset': { borderColor: theme.colors.text },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: theme.colors.secondary || theme.colors.brandSecondary,
                                        fontSize: '16px',
                                        '&.Mui-focused': { color: theme.colors.text },
                                    },
                                    '& .MuiFormHelperText-root': {
                                        color: theme.colors.text,
                                        opacity: 0.8,
                                    },
                                }}
                            />

                            <FormControl fullWidth>
                                <InputLabel sx={{ color: theme.colors.secondary || theme.colors.brandSecondary, fontSize: '16px', '&.Mui-focused': { color: theme.colors.text } }}>Rule Type</InputLabel>
                                <Select
                                    value={ruleType}
                                    onChange={(e) => setRuleType(e.target.value)}
                                    label="Rule Type"
                                    sx={{
                                        color: theme.colors.text,
                                        fontSize: '16px',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.colors.secondary || theme.colors.brandSecondary,
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.colors.text,
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: theme.colors.text,
                                        },
                                    }}
                                >
                                    <MenuItem value="minus">Minus (Fixed Amount)</MenuItem>
                                    <MenuItem value="percent">Percent Split</MenuItem>
                                </Select>
                            </FormControl>

                            {ruleType === 'minus' && (
                                <TextField
                                    label="Amount"
                                    type="number"
                                    value={amount}
                                    onChange={handleAmountChange}
                                    onKeyDown={handleAmountKeyDown}
                                    fullWidth
                                    inputProps={{ min: 0, step: 'any' }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: theme.colors.text,
                                            fontSize: '16px',
                                            '& fieldset': {
                                                borderColor: theme.colors.secondary || theme.colors.brandSecondary,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme.colors.text,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme.colors.text,
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme.colors.secondary || theme.colors.brandSecondary,
                                            fontSize: '16px',
                                            '&.Mui-focused': {
                                                color: theme.colors.text,
                                            },
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '16px',
                                        },
                                    }}
                                />
                            )}

                            {ruleType === 'percent' && (
                                <TextField
                                    label="Rule Percentage (of bill total)"
                                    type="number"
                                    value={rulePercentage}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value === '' || !isNaN(parseFloat(value))) {
                                            setRulePercentage(value);
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                            e.preventDefault();
                                            const currentValue = rulePercentage === '' ? 0 : parseFloat(rulePercentage);
                                            const newValue = e.key === 'ArrowUp' ? currentValue + 1 : currentValue - 1;
                                            if (newValue >= 0 && newValue <= 100) {
                                                setRulePercentage(newValue.toString());
                                            }
                                        }
                                    }}
                                    fullWidth
                                    inputProps={{ min: 0, max: 100, step: 'any' }}
                                    required
                                    error={selectedParticipants.length > 0 && Math.abs(calculateTotalPercentage() - parseFloat(rulePercentage || 0)) >= 0.01}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: theme.colors.text,
                                            fontSize: '16px',
                                            '& fieldset': {
                                                borderColor: theme.colors.secondary || theme.colors.brandSecondary,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: theme.colors.text,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: theme.colors.text,
                                            },
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme.colors.secondary || theme.colors.brandSecondary,
                                            fontSize: '16px',
                                            '&.Mui-focused': {
                                                color: theme.colors.text,
                                            },
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '16px',
                                        },
                                    }}
                                />
                            )}

                            {ruleType === 'percent' && selectedParticipants.length > 0 && (
                                <Box>
                                    <Button
                                        onClick={() => setShowAdvanced(!showAdvanced)}
                                        variant="text"
                                        size="small"
                                        sx={{
                                            color: theme.colors.secondary || theme.colors.brandSecondary,
                                            textTransform: 'none',
                                            mb: 1,
                                            '&:hover': {
                                                backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                            },
                                        }}
                                    >
                                        {showAdvanced ? 'Hide Advanced' : 'Show Advanced'}
                                    </Button>
                                    {showAdvanced && (
                                        <>
                                            <ParticipantSlider
                                                participants={selectedParticipants}
                                                participantPercentages={participantPercentages}
                                                onPercentageChange={(key, newPercentage) => {
                                                    setParticipantPercentages(prev => ({
                                                        ...prev,
                                                        [key]: newPercentage
                                                    }));
                                                }}
                                                theme={theme}
                                                maxPercentage={rulePercentage ? parseFloat(rulePercentage) : 100}
                                            />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: (() => {
                                                        const total = calculateTotalPercentage();
                                                        const target = parseFloat(rulePercentage || 0);
                                                        return Math.abs(total - target) < 0.01 ? theme.colors.text : (theme.colors.error || '#f44336');
                                                    })(),
                                                    mt: 1,
                                                    fontWeight: (() => {
                                                        const total = calculateTotalPercentage();
                                                        const target = parseFloat(rulePercentage || 0);
                                                        return Math.abs(total - target) < 0.01 ? 400 : 600;
                                                    })()
                                                }}
                                            >
                                                {(() => {
                                                    const total = calculateTotalPercentage();
                                                    const target = parseFloat(rulePercentage || 0);
                                                    const diff = Math.abs(total - target);
                                                    return `Total: ${FormatNumber(total)}%${diff >= 0.01 ? ` (must equal ${rulePercentage}%)` : ''}`;
                                                })()}
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                            )}

                            {ruleType === 'percent' && selectedParticipants.length > 0 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {selectedParticipants.map((participant) => {
                                        const key = participant?.id || participant?.email;
                                        if (!key) return null;
                                        const percentage = participantPercentages[key] || 0;

                                        return (
                                            <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                {showAdvanced ? (
                                                    <TextField
                                                        type="number"
                                                        value={percentage || ''}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            const numValue = value === '' ? 0 : parseFloat(value);
                                                            if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
                                                                setParticipantPercentages(prev => ({
                                                                    ...prev,
                                                                    [key]: numValue
                                                                }));
                                                            }
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                                                e.preventDefault();
                                                                const currentValue = percentage || 0;
                                                                const newValue = e.key === 'ArrowUp' ? currentValue + 1 : currentValue - 1;
                                                                if (newValue >= 0 && newValue <= 100) {
                                                                    setParticipantPercentages(prev => ({
                                                                        ...prev,
                                                                        [key]: newValue
                                                                    }));
                                                                }
                                                            }
                                                        }}
                                                        inputProps={{ min: 0, max: 100, step: 'any' }}
                                                        error={Math.abs(calculateTotalPercentage() - parseFloat(rulePercentage || 0)) >= 0.01}
                                                        sx={{
                                                            width: '80px',
                                                            '& .MuiOutlinedInput-root': {
                                                                color: theme.colors.text,
                                                                fontSize: '16px',
                                                                '& fieldset': {
                                                                    borderColor: theme.colors.secondary || theme.colors.brandSecondary,
                                                                },
                                                                '&:hover fieldset': {
                                                                    borderColor: theme.colors.text,
                                                                },
                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: theme.colors.text,
                                                                },
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                fontSize: '16px',
                                                                textAlign: 'right',
                                                                paddingRight: '8px',
                                                            },
                                                        }}
                                                    />
                                                ) : (
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            width: '80px',
                                                            color: theme.colors.text,
                                                            fontSize: '16px',
                                                            textAlign: 'right',
                                                            paddingRight: '8px',
                                                        }}
                                                    >
                                                        {FormatNumber(percentage)}%
                                                    </Typography>
                                                )}
                                                <Typography variant="body2" sx={{ color: theme.colors.text, flex: 1, fontSize: '16px' }}>
                                                    {participant?.email || 'Unknown'}
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    onClick={() => {
                                                        const newParticipants = selectedParticipants.filter(p => {
                                                            const pKey = p?.id || p?.email;
                                                            return pKey !== key;
                                                        });
                                                        setSelectedParticipants(newParticipants);

                                                        // Remove from percentages and redistribute remaining
                                                        const newPercentages = { ...participantPercentages };
                                                        delete newPercentages[key];

                                                        if (newParticipants.length > 0) {
                                                            // Redistribute evenly among remaining participants
                                                            const rulePct = rulePercentage ? parseFloat(rulePercentage) : 100;
                                                            const equalPercent = rulePct / newParticipants.length;
                                                            newParticipants.forEach(p => {
                                                                const pKey = p?.id || p?.email;
                                                                if (pKey) {
                                                                    newPercentages[pKey] = equalPercent;
                                                                }
                                                            });
                                                        }

                                                        setParticipantPercentages(newPercentages);
                                                    }}
                                                    sx={{
                                                        minWidth: 'auto',
                                                        color: theme.colors.error || '#f44336',
                                                        '&:hover': {
                                                            backgroundColor: `${theme.colors.error || '#f44336'}10`,
                                                        },
                                                    }}
                                                >
                                                    Remove
                                                </Button>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}

                            <Autocomplete
                                key={ruleType === 'percent' ? `percent-${selectedParticipants.length}` : 'minus'}
                                multiple={ruleType !== 'percent'}
                                freeSolo
                                options={(() => {
                                    const list = participants || [];
                                    const seen = new Set();
                                    const deduped = list.filter(p => {
                                        if (!p || !p.id) return false;
                                        const email = (p.email ?? '').toString().trim().toLowerCase();
                                        if (!email || seen.has(email)) return false;
                                        seen.add(email);
                                        return !selectedParticipants.some(sp => sp && (sp.id === p.id || (sp.email ?? '').toString().toLowerCase() === email));
                                    });
                                    return deduped;
                                })()}
                                getOptionLabel={(option) => {
                                    if (!option) return '';
                                    return typeof option === 'string' ? option : (option.email || '');
                                }}
                                isOptionEqualToValue={(option, value) => {
                                    const a = typeof option === 'string' ? option : (option?.email ?? '');
                                    const b = typeof value === 'string' ? value : (value?.email ?? '');
                                    return a.toLowerCase() === b.toLowerCase();
                                }}
                                value={ruleType === 'percent' ? null : (selectedParticipants || [])}
                                inputValue={ruleType === 'percent' ? participantInputValue : undefined}
                                onInputChange={ruleType === 'percent' ? (event, newInputValue) => {
                                    if (event) {
                                        setParticipantInputValue(newInputValue);
                                    }
                                } : undefined}
                                loading={isLoadingParticipants}
                                onChange={(event, newValue) => {
                                    if (ruleType === 'percent') {
                                        // For percent rules, add one participant at a time
                                        if (newValue) {
                                            const item = newValue;
                                            let newParticipant = null;

                                            if (typeof item === 'string') {
                                                const email = item.trim();
                                                if (email) {
                                                    newParticipant = { email };
                                                }
                                            } else if (item && item.id && item.email) {
                                                newParticipant = item;
                                            }

                                            if (newParticipant) {
                                                const email = newParticipant.email?.toLowerCase();
                                                const alreadyExists = selectedParticipants.find(sp => {
                                                    const spEmail = sp.email?.toLowerCase();
                                                    return spEmail === email || sp.id === newParticipant.id;
                                                });

                                                if (!alreadyExists) {
                                                    const updatedParticipants = [...selectedParticipants, newParticipant];
                                                    setSelectedParticipants(updatedParticipants);

                                                    // Redistribute percentages evenly
                                                    const rulePct = rulePercentage ? parseFloat(rulePercentage) : 100;
                                                    const equalPercent = rulePct / updatedParticipants.length;
                                                    const newPercentages = {};
                                                    updatedParticipants.forEach(p => {
                                                        const key = p.id || p.email;
                                                        if (key) {
                                                            newPercentages[key] = equalPercent;
                                                        }
                                                    });
                                                    setParticipantPercentages(newPercentages);
                                                    
                                                    // Clear the input field
                                                    setParticipantInputValue('');
                                                }
                                            }
                                        }
                                    } else {
                                        // For minus rules, handle multiple participants
                                        const raw = Array.isArray(newValue) ? newValue : [];
                                        const processedParticipants = [];
                                        const seenEmails = new Set();

                                        raw.forEach(item => {
                                            if (typeof item === 'string') {
                                                const email = item.trim().toLowerCase();
                                                if (email && !seenEmails.has(email)) {
                                                    processedParticipants.push({ email: item.trim() });
                                                    seenEmails.add(email);
                                                }
                                            } else if (item && (item.id || item.email)) {
                                                const email = (item.email ?? '').toString().toLowerCase();
                                                if (email && !seenEmails.has(email)) {
                                                    processedParticipants.push(item);
                                                    seenEmails.add(email);
                                                }
                                            }
                                        });

                                        setSelectedParticipants(processedParticipants);
                                    }
                                }}
                                renderTags={(value, getTagProps) =>
                                    ruleType !== 'percent' ? value.map((option, index) => {
                                        const email = option.email || option;
                                        const { key, ...tagProps } = getTagProps({ index });
                                        return (
                                            <Chip
                                                key={key}
                                                label={email}
                                                {...tagProps}
                                                sx={{ color: theme.colors.text }}
                                            />
                                        );
                                    }) : []
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={ruleType === 'percent' ? 'Add Participant' : 'Participants'}
                                        placeholder="Select or type email"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                const inputEl = e.target;
                                                const hasText = (inputEl?.value ?? '').toString().trim() !== '';
                                                if (hasText) {
                                                    e.preventDefault();
                                                }
                                            }
                                            params.onKeyDown?.(e);
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                color: theme.colors.text,
                                                fontSize: '16px',
                                                '& fieldset': {
                                                    borderColor: theme.colors.secondary || theme.colors.brandSecondary,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: theme.colors.text,
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: theme.colors.text,
                                                },
                                            },
                                            '& .MuiInputLabel-root': {
                                                color: theme.colors.secondary || theme.colors.brandSecondary,
                                                fontSize: '16px',
                                                '&.Mui-focused': {
                                                    color: theme.colors.text,
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />

                            <Box sx={{ width: '100%' }}>
                                <Button
                                    type={selectedParticipants.length > 0 && isValid() ? 'submit' : 'button'}
                                    variant="outlined"
                                    fullWidth
                                    disabled={selectedParticipants.length > 0 && (submitting || !isValid())}
                                    onClick={selectedParticipants.length > 0 ? undefined : handleCancel}
                                    sx={{
                                        border: selectedParticipants.length > 0 && isValid()
                                            ? `1px solid ${theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary}`
                                            : `1px solid ${theme.colors.border || '#ccc'}`,
                                        backgroundColor: 'transparent',
                                        color: selectedParticipants.length > 0 && isValid()
                                            ? (theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary)
                                            : theme.colors.text,
                                        '&:hover': {
                                            borderColor: selectedParticipants.length > 0 && isValid()
                                                ? (theme.colors.secondary || theme.colors.primary || theme.colors.brandSecondary)
                                                : (theme.colors.border || '#ccc'),
                                            backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                        },
                                        '&:disabled': {
                                            borderColor: theme.colors.border || '#ccc',
                                            color: theme.colors.text,
                                            opacity: 0.5,
                                        },
                                    }}
                                >
                                    {submitting ? 'Adding...' : (selectedParticipants.length > 0 ? 'Done' : 'Cancel')}
                                </Button>
                            </Box>
                        </Box>
                    </TableCell>
                    <TableCell sx={{
                        width: '56px !important',
                        minWidth: '56px !important',
                        maxWidth: '56px !important',
                        p: 0,
                        flexShrink: 0,
                        flexGrow: 0,
                    }}></TableCell>
                </TableRow>
            )}

            {/* Add Button Row */}
            {!isCreating && !disabled && (
                <TableRow>
                    <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }}></TableCell>
                    <TableCell colSpan={2} sx={{ pl: 0, pr: 0, py: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={handleStartAdd}
                            startIcon={<AddIcon />}
                            fullWidth
                            sx={{
                                borderColor: theme.current === 'light'
                                    ? theme.colors.brandSecondary
                                    : (theme.colors.secondary || theme.colors.brandSecondary),
                                color: theme.current === 'light'
                                    ? '#000000'
                                    : (theme.colors.secondary || theme.colors.brandSecondary),
                                backgroundColor: theme.current === 'light'
                                    ? theme.colors.brandSecondary
                                    : (theme.colors.tableBackground || theme.colors.background),
                                '&:hover': {
                                    borderColor: theme.current === 'light'
                                        ? theme.colors.brandSecondary
                                        : (theme.colors.secondary || theme.colors.brandSecondary),
                                    backgroundColor: theme.current === 'light'
                                        ? theme.colors.brandSecondary
                                        : (theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)'),
                                },
                            }}
                        >
                            Add Rule
                        </Button>
                    </TableCell>
                    <TableCell sx={{ width: '56px', minWidth: '56px', maxWidth: '56px', p: 0 }}></TableCell>
                </TableRow>
            )}
        </>
    );
};

export default CreateRuleForm;
